import { useNavigate } from "react-router-dom";
import { Brain, Heart, Phone, TrendingUp, ChevronRight, ChevronLeft, BookMarked } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import { dbGet } from "../lib/db";
import DayEditor from "./DayEditor";

// ─── Quotes ───────────────────────────────────────────────────────────────────

const QUOTES = [
  "Tu cerebro no está roto, está secuestrado. Y los cerebros secuestrados pueden liberarse.",
  "La neuroplasticidad es tu superpoder. Cada nueva conducta sana recablea tu cerebro.",
  "La adicción no es una falla moral. Es una enfermedad cerebral crónica y tratable.",
  "Cada día sin usar es evidencia de que tu corteza prefrontal se está fortaleciendo.",
  "La recuperación no es una línea recta, pero siempre se puede volver al camino.",
  "Pedir ayuda no es debilidad. Es tu cerebro usando el sistema que más lo protege: la conexión.",
];

const QUICK_CARDS = [
  { path: "/tools",   icon: Heart,      label: "Herramientas", sub: "Respiración · HALT · Mindfulness", color: "bg-[#EEF4F1] text-[#6B9080]",  testid: "quick-tools" },
  { path: "/library", icon: Brain,      label: "Biblioteca",   sub: "26 recursos científicos",           color: "bg-[#EDF4FF] text-[#5B8FCC]",  testid: "quick-library" },
  { path: "/support", icon: Phone,      label: "Red de Apoyo", sub: "Contactos de emergencia",           color: "bg-[#FFF4EE] text-[#C47840]",  testid: "quick-support" },
  { path: "/journal", icon: BookMarked, label: "Diario",       sub: "Check-in · Momentos · Historial",  color: "bg-[#F3EEF9] text-[#8B6BAE]",  testid: "quick-journal" },
];

// ─── Storage keys ─────────────────────────────────────────────────────────────

const SK  = "reconectados_journal";
const MK  = "reconectados_morning";
const MOK = "reconectados_moments";

// ─── Calendar helpers ─────────────────────────────────────────────────────────

const WEEKDAYS  = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sá", "Do"];
const MONTHS_ES = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];

function isoDate(d) { return d.toISOString().slice(0, 10); }

function buildDots(eveningList, morningMap, allMoments) {
  const dots = {};
  eveningList.forEach(e => {
    const k = e.date.slice(0, 10);
    if (!dots[k]) dots[k] = new Set();
    dots[k].add("evening");
  });
  Object.keys(morningMap).forEach(k => {
    if (!dots[k]) dots[k] = new Set();
    dots[k].add("morning");
  });
  allMoments.forEach(m => {
    const k = m.time.slice(0, 10);
    if (!dots[k]) dots[k] = new Set();
    dots[k].add("moment");
  });
  return dots;
}

// ─── Calendar widget ──────────────────────────────────────────────────────────

function MiniCalendar({ dots, selectedDay, onSelectDay }) {
  const today = new Date();
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  const prevMonth = () => { if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1); };

  const firstDay    = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayKey    = isoDate(today);

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const handleDay = (day) => {
    const key = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    onSelectDay(key);
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E4EBE6] p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <button onClick={prevMonth} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-[#F7F9F7] transition-colors">
          <ChevronLeft size={16} className="text-[#2C4238]" />
        </button>
        <p className="text-sm font-semibold text-[#1E2F23] capitalize" style={{ fontFamily: "Manrope" }}>
          {MONTHS_ES[month]} {year}
        </p>
        <button onClick={nextMonth} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-[#F7F9F7] transition-colors">
          <ChevronRight size={16} className="text-[#2C4238]" />
        </button>
      </div>

      <div className="grid grid-cols-7 mb-1">
        {WEEKDAYS.map(d => (
          <div key={d} className="text-center text-xs font-semibold text-[#4A6E5C] py-1">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-y-1">
        {cells.map((day, i) => {
          if (!day) return <div key={`e-${i}`} />;
          const key        = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const isToday    = key === todayKey;
          const isSelected = key === selectedDay;
          const dayDots    = dots[key];
          return (
            <button key={key} onClick={() => handleDay(day)}
              className={`relative flex flex-col items-center justify-center py-1.5 rounded-xl transition-all ${
                isSelected ? "bg-[#6B9080] text-white" : isToday ? "bg-[#EEF4F1] text-[#6B9080]" : "text-[#1E2F23] hover:bg-[#F7F9F7]"
              }`}>
              <span className={`text-xs font-medium leading-none ${isToday && !isSelected ? "font-bold" : ""}`}>{day}</span>
              {dayDots && (
                <div className="flex gap-0.5 mt-0.5">
                  {dayDots.has("morning") && <div className={`w-1 h-1 rounded-full ${isSelected ? "bg-white/80" : "bg-[#F4A261]"}`} />}
                  {dayDots.has("moment")  && <div className={`w-1 h-1 rounded-full ${isSelected ? "bg-white/80" : "bg-[#5B8FCC]"}`} />}
                  {dayDots.has("evening") && <div className={`w-1 h-1 rounded-full ${isSelected ? "bg-white/80" : "bg-[#6B9080]"}`} />}
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#F0F5F2]">
        <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-[#F4A261]" /><span className="text-xs text-[#2C4238]">Mañana</span></div>
        <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-[#5B8FCC]" /><span className="text-xs text-[#2C4238]">Momentos</span></div>
        <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-[#6B9080]" /><span className="text-xs text-[#2C4238]">Noche</span></div>
      </div>
    </div>
  );
}

// ─── Home ─────────────────────────────────────────────────────────────────────

export default function Home() {
  const navigate = useNavigate();
  const [quote, setQuote]             = useState("");
  const [riskLevel, setRiskLevel]     = useState(null);
  const [streak, setStreak]           = useState(0);
  const [dots, setDots]               = useState({});
  const [selectedDay, setSelectedDay] = useState(null);
  const [editOpen, setEditOpen]       = useState(false);

  const loadDots = useCallback(async () => {
    const [evening, mornings, moments, s] = await Promise.all([
      dbGet(SK, []),
      dbGet(MK, {}),
      dbGet(MOK, []),
      dbGet("reconectados_streak", 0),
    ]);
    setDots(buildDots(evening, mornings, moments));
    if (evening.length > 0) setRiskLevel(evening[evening.length - 1].riskLevel);
    setStreak(s);
  }, []);

  useEffect(() => {
    setQuote(QUOTES[Math.floor(Math.random() * QUOTES.length)]);
    loadDots();
  }, [loadDots]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Buenos días" : hour < 19 ? "Buenas tardes" : "Buenas noches";

  const handleSelectDay = (key) => {
    setSelectedDay(prev => {
      if (prev === key) { setEditOpen(false); return null; }
      setEditOpen(false);
      return key;
    });
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div className="animate-fade-in-up mb-6">
        <p className="text-xl font-semibold text-[#1E2F23]" style={{ fontFamily: "Manrope, sans-serif" }}>
          {greeting} 👋
        </p>
        <p className="text-sm text-[#2C4238] mt-0.5">La ciencia de la recuperación</p>
      </div>


      {/* Stats */}
      {(streak > 0 || riskLevel !== null) && (
        <div className="animate-fade-in-up delay-200 flex gap-3 mb-6">
          {streak > 0 && (
            <div className="flex-1 bg-white rounded-2xl p-4 border border-[#E4EBE6]">
              <div className="flex items-center gap-2">
                <TrendingUp size={16} className="text-[#6B9080]" />
                <span className="text-xs text-[#2C4238] font-medium">Racha</span>
              </div>
              <p className="text-2xl font-bold text-[#1E2F23] mt-1">{streak} <span className="text-sm font-normal text-[#2C4238]">días</span></p>
            </div>
          )}
          {riskLevel !== null && (
            <div className="flex-1 bg-white rounded-2xl p-4 border border-[#E4EBE6]">
              <span className="text-xs text-[#2C4238] font-medium">Riesgo hoy</span>
              <p className={`text-2xl font-bold mt-1 ${riskLevel <= 3 ? "text-[#84A98C]" : riskLevel <= 6 ? "text-[#F4A261]" : "text-[#E07A5F]"}`}>
                {riskLevel}/10
              </p>
            </div>
          )}
        </div>
      )}

      {/* Calendar */}
      <div className="animate-fade-in-up delay-200 mb-1">
        <h2 className="text-base font-semibold text-[#1E2F23] mb-3" style={{ fontFamily: "Manrope" }}>Mi registro</h2>
        <MiniCalendar dots={dots} selectedDay={selectedDay} onSelectDay={handleSelectDay} />
      </div>

      {/* Edit button for selected day */}
      {selectedDay && (
        <div className="flex items-center justify-between mb-5 px-1 animate-fade-in">
          <p className="text-xs text-[#2C4238] capitalize">
            {new Date(selectedDay + "T12:00:00").toLocaleDateString("es-ES", {
              weekday: "long", day: "numeric", month: "long",
            })}
          </p>
          {selectedDay === isoDate(new Date()) ? (
            <button
              onClick={() => navigate("/journal")}
              className="px-4 py-2 bg-[#EEF4F1] text-[#6B9080] text-xs font-semibold rounded-xl active:scale-95 transition-transform"
            >
              Ir a Hoy →
            </button>
          ) : (
            <button
              onClick={() => setEditOpen(true)}
              className="px-4 py-2 bg-[#6B9080] text-white text-xs font-semibold rounded-xl active:scale-95 transition-transform"
            >
              Editar día
            </button>
          )}
        </div>
      )}

      {/* Bottom-sheet modal editor */}
      {editOpen && selectedDay && (
        <div
          className="fixed inset-0 z-50 bg-black/50"
          onClick={() => setEditOpen(false)}
        >
          <div
            className="absolute bottom-0 left-0 right-0 max-h-[90vh] overflow-y-auto bg-[#F7F9F7] rounded-t-3xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-[#D1D9D5]" />
            </div>
            <div className="px-4 pb-10">
              <DayEditor
                key={selectedDay}
                dayKey={selectedDay}
                onClose={() => setEditOpen(false)}
                onSaved={() => { loadDots(); }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Quick access */}
      <div className="animate-fade-in-up delay-300 mb-6">
        <h2 className="text-base font-semibold text-[#1E2F23] mb-3" style={{ fontFamily: "Manrope" }}>Acceso rápido</h2>
        <div className="grid grid-cols-2 gap-3">
          {QUICK_CARDS.map(({ path, icon: Icon, label, sub, color, testid }) => (
            <button key={path} data-testid={testid} onClick={() => navigate(path)}
              className="card-hover bg-white rounded-2xl p-4 border border-[#E4EBE6] text-left flex flex-col gap-2">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}>
                <Icon size={18} />
              </div>
              <div>
                <p className="font-semibold text-sm text-[#1E2F23]" style={{ fontFamily: "Manrope" }}>{label}</p>
                <p className="text-xs text-[#2C4238] mt-0.5">{sub}</p>
              </div>
              <ChevronRight size={14} className="text-[#4A6E5C] self-end" />
            </button>
          ))}
        </div>
      </div>

      {/* Daily quote */}
      <div className="animate-fade-in-up delay-400 bg-white rounded-2xl p-5 border border-[#E4EBE6] mb-4">
        <p className="text-xs text-[#6B9080] font-semibold uppercase tracking-wide mb-2">Reflexión del día</p>
        <p className="text-sm text-[#1E2F23] leading-relaxed italic">"{quote}"</p>
        <p className="text-xs text-[#4A6E5C] mt-2">— Mi Diario</p>
      </div>

      {/* Legal */}
      <div className="animate-fade-in-up delay-500 bg-[#FFF9F7] rounded-2xl p-4 border border-[#F4C5B5]">
        <p className="text-xs text-[#8B5A48] leading-relaxed">
          <span className="font-semibold">Aviso legal:</span> Esta app es una herramienta educativa basada en evidencia científica. No sustituye atención médica ni psicológica profesional. En emergencias: llama al 112 (España), 911 (México/USA).
        </p>
      </div>
    </div>
  );
}
